import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Badge, ErrorBox } from '../components/common';
import { formatDate } from '../labels';
import { EMPTY_IDENTITY, IdentityFields, type IdentityForm, identityPayload } from './Registry';
import { ApplicationsListPage } from './Demandes';
import '../styles/pilotage.css';

type Masked = number | '<5';
interface Indicators {
  notice: string;
  demandes: {
    parStatut: Record<string, Masked>;
    stock: Masked;
    parMois: { month: string; deposees: Masked; cloturees: Masked; delai_median_jours: number | null }[];
  };
  bulletinsDelivres: Masked;
  transmissions: {
    juridiction: string;
    a_valider: Masked;
    a_corriger: Masked;
    validees: Masked;
    rejetees: Masked;
    en_retard: Masked;
  }[];
  archives: {
    juridiction: string;
    lots: number;
    pages_declarees: number;
    pages_capturees: number;
    pieces_integrees: number;
  }[];
  couverture: { juridiction: string; statut: string | null }[];
  finance: { paiements_confirmes: Masked; montant_confirme_xaf: number; anomalies_ouvertes: number };
}
interface Alerts {
  generatedAt: string;
  alerts: { niveau: 'critique' | 'attention'; type: string; message: string; acteurs?: string[] }[];
}

const STATUS_LABELS: Record<string, string> = {
  deposee: 'Déposées',
  complement_demande: 'Complément demandé',
  paiement_attendu: 'Paiement attendu',
  traitement_en_cours: 'En traitement',
  verification_complementaire: 'Vérification complémentaire',
  document_disponible: 'Document disponible',
  refusee: 'Refusées',
  cloturee: 'Clôturées',
};
const COV: Record<string, { label: string; tone: string }> = {
  complete_attestee: { label: 'Complète attestée', tone: 'ok' },
  partielle: { label: 'Partielle', tone: 'warn' },
  non_reprise: { label: 'Non reprise', tone: 'muted' },
};
/**
 * Longueur des barres : un effectif masqué « <5 » n'est jamais dessiné comme une valeur exacte ; sa barre est
 * hachurée et s'arrête à sa borne haute (4). Un zéro n'a pas de barre.
 */
const MASKED_UPPER_BOUND = 4;
const upper = (v: Masked) => (v === '<5' ? MASKED_UPPER_BOUND : v);
const barWidth = (value: Masked, max: number) => {
  const v = upper(value);
  return max && v ? Math.max(2, Math.round((v / max) * 100)) : 0;
};

/** Pictogrammes au trait des indicateurs et alertes (maquette S20), décoratifs. */
const LINE_PATHS = {
  inbox: 'M3 13h5l1 3h6l1-3h5M5 5h14l2 8v6H3v-6z',
  send: 'M22 2 11 13M22 2l-7 20-4-9-9-4z',
  coin: 'M15 9h-4a2 2 0 0 0 0 4h2a2 2 0 0 1 0 4H9M12 6v2M12 16v2',
  list: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  check: 'm5 12 5 5 9-10',
  warn: 'M12 3 2 21h20zM12 10v4M12 18h.01',
} as const;

function LineIcon({ name, circle }: { name: keyof typeof LINE_PATHS; circle?: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {circle && <circle cx="12" cy="12" r="9" />}
      <path d={LINE_PATHS[name]} />
    </svg>
  );
}

function Stat({
  icon,
  value,
  label,
  circle,
}: {
  icon: keyof typeof LINE_PATHS;
  value: React.ReactNode;
  label: React.ReactNode;
  circle?: boolean;
}) {
  return (
    <div className="stat">
      <span className="ic">
        <LineIcon name={icon} circle={circle} />
      </span>
      <span className="v">{value}</span>
      <span className="l">{label}</span>
    </div>
  );
}

function Bar({ value, max, label }: { value: Masked; max: number; label: string }) {
  const width = barWidth(value, max);
  return (
    <div className="pl-bar">
      <span className="muted mono">{label}</span>
      <span className="pl-track" aria-hidden="true">
        <span className={value === '<5' ? 'pl-fill pl-masked' : 'pl-fill'} style={{ width: `${width}%` }} />
      </span>
      <span className="pl-val">{value}</span>
    </div>
  );
}

function StatusBar({ value, max, label }: { value: Masked; max: number; label: string }) {
  const width = barWidth(value, max);
  return (
    <div className="pl-status">
      <span>{label}</span>
      <span className="pl-val">{value}</span>
      <span className="pl-track" aria-hidden="true">
        <span className={value === '<5' ? 'pl-fill pl-masked' : 'pl-fill'} style={{ width: `${width}%` }} />
      </span>
    </div>
  );
}

export function AlertsCard() {
  const [data, setData] = useState<Alerts | null>(null);
  useEffect(() => {
    api<Alerts>('/pilotage/alertes')
      .then(setData)
      .catch(() => setData(null));
  }, []);
  if (!data) return null;
  return (
    <section className="pl-alerts" aria-labelledby="pl-alerts-h">
      <h2 id="pl-alerts-h" className={data.alerts.length === 0 ? 'visually-hidden' : undefined}>
        Alertes
      </h2>
      {data.alerts.length === 0 ? (
        <div className="alert alert-ok pl-alert">
          <LineIcon name="check" />
          <span className="pl-alert-body">Aucune alerte au {formatDate(data.generatedAt)}.</span>
        </div>
      ) : (
        data.alerts.map((a) => (
          <div
            key={a.type}
            className={`alert pl-alert ${a.niveau === 'critique' ? 'alert-danger' : 'alert-warn'}`}
          >
            <LineIcon name="warn" />
            <div className="pl-alert-body">
              <strong>{a.niveau === 'critique' ? 'Critique' : 'Attention'} :</strong> {a.message}
              {a.acteurs && (
                <div className="small">Comptes (identifiants internes) : {a.acteurs.join(', ')}</div>
              )}
            </div>
          </div>
        ))
      )}
    </section>
  );
}

export function PilotagePage() {
  const [d, setD] = useState<Indicators | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  useEffect(() => {
    api<Indicators>('/pilotage/indicateurs').then(setD).catch(setError);
  }, []);
  if (!d) return error ? <ErrorBox error={error} /> : <p>Chargement…</p>;
  const maxMonth = Math.max(1, ...d.demandes.parMois.map((m) => upper(m.deposees)));
  const maxStatus = Math.max(1, ...Object.values(d.demandes.parStatut).map(upper));

  return (
    <div className="stack">
      <div className="head">
        <div>
          <h1>Pilotage</h1>
          <p className="lead">{d.notice}</p>
        </div>
      </div>
      <div className="kpis">
        <Stat icon="inbox" value={d.demandes.stock} label="Demandes en cours (stock)" />
        <Stat icon="send" value={d.bulletinsDelivres} label="Bulletins délivrés" />
        <Stat
          icon="coin"
          circle
          value={d.finance.paiements_confirmes}
          label={<>Paiements confirmés - {d.finance.montant_confirme_xaf.toLocaleString('fr-FR')} XAF</>}
        />
        <Stat icon="list" value={d.finance.anomalies_ouvertes} label="Anomalies financières ouvertes" />
      </div>
      <AlertsCard />
      <div className="cols">
        <section className="card flow">
          <h2>Demandes déposées par mois</h2>
          <div className="pl-bars">
            {d.demandes.parMois.map((m) => (
              <Bar key={m.month} label={m.month} value={m.deposees} max={maxMonth} />
            ))}
          </div>
          <p className="pl-note">
            Délai médian de délivrance :{' '}
            {d.demandes.parMois
              .filter((m) => m.delai_median_jours !== null)
              .map((m) => `${m.month} : ${m.delai_median_jours} j`)
              .join(' · ') || 'pas encore de délivrance'}
          </p>
        </section>
        <section className="card flow">
          <h2>Demandes par état</h2>
          <div className="pl-bars">
            {Object.entries(d.demandes.parStatut).map(([k, v]) => (
              <StatusBar key={k} label={STATUS_LABELS[k] ?? k} value={v} max={maxStatus} />
            ))}
          </div>
        </section>
      </div>
      <section className="card np">
        <div className="pl-card-h">
          <h2 id="pl-transmissions-h">Transmissions des juridictions</h2>
        </div>
        <div className="table-wrap">
          <table aria-labelledby="pl-transmissions-h">
            <thead>
              <tr>
                <th scope="col">Juridiction</th>
                <th scope="col">À valider</th>
                <th scope="col">dont en retard</th>
                <th scope="col">À corriger</th>
                <th scope="col">Validées</th>
                <th scope="col">Rejetées</th>
              </tr>
            </thead>
            <tbody>
              {d.transmissions.map((t) => (
                <tr key={t.juridiction}>
                  <td>{t.juridiction}</td>
                  <td>{t.a_valider}</td>
                  <td>{t.en_retard ? <Badge tone="warn">{String(t.en_retard)}</Badge> : 0}</td>
                  <td>{t.a_corriger}</td>
                  <td>{t.validees}</td>
                  <td>{t.rejetees}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="card np">
        <div className="pl-card-h">
          <h2 id="pl-archives-h">Archives et couverture</h2>
        </div>
        <div className="table-wrap">
          <table aria-labelledby="pl-archives-h">
            <thead>
              <tr>
                <th scope="col">Casier</th>
                <th scope="col">Couverture</th>
                <th scope="col">Lots</th>
                <th scope="col">Pages capturées / déclarées</th>
                <th scope="col">Pièces intégrées</th>
              </tr>
            </thead>
            <tbody>
              {d.couverture.map((c) => {
                const a = d.archives.find((x) => x.juridiction === c.juridiction);
                return (
                  <tr key={c.juridiction}>
                    <td>{c.juridiction}</td>
                    <td>
                      <Badge tone={COV[c.statut ?? '']?.tone ?? 'muted'}>
                        {COV[c.statut ?? '']?.label ?? 'Non renseignée'}
                      </Badge>
                    </td>
                    <td>{a?.lots ?? 0}</td>
                    <td>
                      {a?.pages_capturees ?? 0} / {a?.pages_declarees ?? 0}
                    </td>
                    <td>{a?.pieces_integrees ?? 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

// ---------- Organismes habilités ----------

export function OrganismPage() {
  const { me } = useAuth();
  const refs = useReferentialsPurposes();
  const navigate = useNavigate();
  const [identity, setIdentity] = useState<IdentityForm>(EMPTY_IDENTITY);
  const [purpose, setPurpose] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const allowed = [...new Set((me?.habilitations ?? []).flatMap((h) => h.purposes))];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const created = await api<{ id: string }>('/demandes/organisme', {
        method: 'POST',
        body: {
          typeCode: 'bulletin_organisme',
          identity: identityPayload(identity),
          contact: {},
          purpose,
          purposeReason: reason,
        },
      });
      navigate(`/espace/demandes/${created.id}`);
    } catch (err) {
      setError(err as ApiError);
    }
  };

  return (
    <div className="stack">
      <div className="head">
        <div>
          <h1>Organisme habilité</h1>
          <p className="lead">
            Les demandes sont reçues dans les seuls cadres autorisés : une finalité couverte par votre
            habilitation, un motif précis. Aucune recherche générale, aucune extraction massive.
          </p>
        </div>
      </div>
      <form className="card flow" onSubmit={submit}>
        <h2>Nouvelle demande motivée</h2>
        <IdentityFields value={identity} onChange={setIdentity} full />
        <div className="fg">
          <div className="field full">
            <label htmlFor="o-purpose">Finalité</label>
            <select id="o-purpose" required value={purpose} onChange={(e) => setPurpose(e.target.value)}>
              <option value="">- Choisir -</option>
              {allowed.map((p) => (
                <option key={p} value={p}>
                  {refs[p] ?? p}
                </option>
              ))}
            </select>
          </div>
          <div className="field full">
            <label htmlFor="o-reason">Motif précis (20 caractères au moins)</label>
            <textarea
              id="o-reason"
              required
              minLength={20}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
        <ErrorBox error={error} />
        <div className="actions">
          <button className="btn" type="submit" disabled={!purpose || reason.length < 20}>
            Créer la demande
          </button>
        </div>
      </form>
      <ApplicationsListPage mode="usager" />
    </div>
  );
}

function useReferentialsPurposes(): Record<string, string> {
  const [map, setMap] = useState<Record<string, string>>({});
  useEffect(() => {
    api<{ purposes: { code: string; label: string }[] }>('/referentiels')
      .then((r) => setMap(Object.fromEntries(r.purposes.map((p) => [p.code, p.label]))))
      .catch(() => setMap({}));
  }, []);
  return map;
}
